import {
  Button,
  Collapse,
  CollapseProps,
  Input,
  Select,
  Spin,
  message,
} from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as SendIcon } from '../../../../assets/send.svg'
import {
  errorPatientSmsLogs,
  getPatientSmsLogs,
  selectErrorInPatientSmsLogs,
  selectPatientSmsLogsById,
  selectSuccessInPatientSmsLogs,
  sendPatientSms,
  successPatientSmsLogs,
} from '../../../../state/patientSmsLogs/patientSmsLogsReducer'
import { TabEmptyText } from '../../PatientsViewStyle'
import { SmsLogsType } from '../../../../state/patientSmsLogs/types'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Icon from '@ant-design/icons/lib/components/Icon'
import { selectPatientByKey } from '../../../../state/patient/patientReducer'
import { DefaultOptionType } from 'antd/es/select'
import { usePatientTabContext } from '../../PatientTabs'
import { displayMessage } from '../../../../helpers/Message'

type SmsLoggerTabProps = {
  patientKey: string
}

const SmsCollapseTitleDiv = styled.div`
  font-weight: 500;
  font-size: 1.1em;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0px 20px;
  & .dateTimeContainer {
    display: flex;
    gap: 30px;
  }
`

const SmsCollapseContentDiv = styled.div`
  padding: 5px 35px;
`

const smsPanelStyle: React.CSSProperties = {
  marginBottom: 12,
  border: 'none',
  borderRadius: 10,
  marginLeft: 3,
  marginRight: 3,
  boxShadow:
    'rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px',
}

const SmsLoggerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  flex: 1 1 0;
  gap: 10px;
`

const SmsSenderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 10px 3px;
  gap: 20px;
  padding: 10px;
  border-radius: 10px;
  width: 100%;
  /* box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px; */
`

const SmsSenderTextArea = styled(Input)``

const SmsSenderButton = styled(Button)`
  background-color: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: #f5f5f5;
  border: 0px;
  padding: 5px 10px;
  border-radius: 5px;
`

const StyledCollapse = styled(Collapse)`
  width: 100%;
`

function SmsLoggerTab({ patientKey }: SmsLoggerTabProps) {
  const dispatch = useDispatch()
  const [activeCollapse, setActiveCollapse] = useState('')
  const [messageText, setMessageText] = useState('')
  const [messageApi, contextHolder] = message.useMessage()
  const patientSmsLogsError = useSelector(selectErrorInPatientSmsLogs)
  const patientSmsLogsSuccess = useSelector(selectSuccessInPatientSmsLogs)
  const { size } = usePatientTabContext()
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null)

  const patient = useSelector(selectPatientByKey(patientKey))
  const selectItems: DefaultOptionType[] = []

  if (patient?.emergencyPhone) {
    selectItems.push({
      label: `${patient.emergencyPhone} (Primary)`,
      value: patient.emergencyPhone,
    })
  }

  if (patient?.emergencyPhone2) {
    selectItems.push({
      label: `${patient.emergencyPhone2} (Secondary)`,
      value: patient.emergencyPhone2,
    })
  }

  function SmsCollapseTitle({
    smsLog,
    activeCollapse,
  }: {
    smsLog: SmsLogsType
    activeCollapse: string
  }) {
    const timeStampArray = smsLog.createTimestamp.split(' ')
    const date = timeStampArray[0].split('-').reverse().join('/')
    const time = timeStampArray[1]
    return (
      <SmsCollapseTitleDiv>
        <span className='doctor'>{smsLog.sendFrom}</span>
        <div className='dateTimeContainer'>
          <span>Sent To: {smsLog.sendTo}</span>
          <span className='date'> on {date}</span>
          <span className='time'> at {time}</span>
        </div>
      </SmsCollapseTitleDiv>
    )
  }

  const smsLogs = useSelector(selectPatientSmsLogsById(patientKey))!

  const smsLogItems: CollapseProps['items'] = useMemo(() => {
    return smsLogs?.map((smsLog) => {
      return {
        key: smsLog.smsId,
        label: (
          <SmsCollapseTitle activeCollapse={activeCollapse} smsLog={smsLog} />
        ),
        children: (
          <SmsCollapseContentDiv>{smsLog.message}</SmsCollapseContentDiv>
        ),
        style: smsPanelStyle,
      }
    })
  }, [smsLogs])
  const sendButtonClick = () => {
    dispatch(
      sendPatientSms({
        patientKey: patientKey,
        smsMsg: messageText,
      })
    )
    setMessageText('')
    setSelectedPhoneNumber(null)
  }
  useEffect(() => {
    dispatch(getPatientSmsLogs(patientKey))
  }, [])

  useEffect(() => {
    if (patientSmsLogsError) {
      displayMessage(patientSmsLogsError, 'error', messageApi)
      dispatch(errorPatientSmsLogs(null))
    }
    if (patientSmsLogsSuccess) {
      displayMessage(patientSmsLogsSuccess, 'success', messageApi)
      dispatch(successPatientSmsLogs(null))
    }
  }, [patientSmsLogsError, patientSmsLogsSuccess])
  return (
    <SmsLoggerContainer>
      {contextHolder}
      <SmsSenderContainer>
        <SmsSenderTextArea
          size={size}
          value={messageText}
          onChange={(event) => {
            setMessageText(event.target.value)
          }}
        />
        <Select
          size={size}
          style={{ width: '350px' }}
          options={selectItems}
          placeholder='Select the Phone Number'
          value={selectedPhoneNumber}
          onChange={(value) => {
            setSelectedPhoneNumber(value)
          }}
        />
        <SmsSenderButton size={size} onClick={sendButtonClick}>
          <Icon component={SendIcon} />
        </SmsSenderButton>
      </SmsSenderContainer>
      {!smsLogs ? (
        <Spin size='large' />
      ) : (
        <>
          {smsLogItems.length > 0 ? (
            <StyledCollapse
              size={size}
              onChange={(ids) => {
                setActiveCollapse(ids[0])
              }}
              accordion
              bordered={false}
              className='smsLogAccordion'
              items={smsLogItems}
            />
          ) : (
            <TabEmptyText>No SMS Logs Data</TabEmptyText>
          )}
        </>
      )}
    </SmsLoggerContainer>
  )
}

export default SmsLoggerTab
